import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { API_LOCALE, isLanguage } from '@/i18n'
import { useAsync } from '@/lib/useAsync'
import type { Async } from '@/lib/useAsync'
import { accesosService } from '@/services/accesos'
import type { KeyStatus } from '@/services/accesos'
import { checkinService } from '@/services/checkin'
import type { Arrival } from '@/services/checkin'
import { climaService } from '@/services/clima'
import type { Climate } from '@/services/clima'
import { extrasService } from '@/services/extras'
import type { Recommendation } from '@/services/extras'
import { fianzaService } from '@/services/fianza'
import type { Deposit } from '@/services/fianza'
import { pagosService } from '@/services/pagos'
import type { PaymentSummary } from '@/services/pagos'
import { reservaService } from '@/services/reserva'
import type { PendingChangePayment, Stay } from '@/services/reserva'

export interface StayData {
  payment: Async<PaymentSummary>
  deposit: Async<Deposit | null>
  recommendation: Async<Recommendation | null>
  key: Async<KeyStatus>
  climate: Async<Climate>
  arrival: Async<Arrival | null>
  /** Cambio de reserva aplicado y sin pagar (D45) */
  pendingChange: Async<PendingChangePayment | null>
  reload: () => void
}

/** Carga en paralelo todo lo que Tu estancia necesita además de la reserva; cada parte tiene su propio estado. */
export function useStayData(stay: Stay): StayData {
  const { i18n } = useTranslation()
  const lang = isLanguage(i18n.language) ? i18n.language : 'es'
  const apiLang = API_LOCALE[lang]
  const key = `${stay.guestCode}|${stay.phase}|${lang}`
  const [payment, r1] = useAsync(key, (signal) => pagosService.getPaymentSummary(stay, apiLang, signal))
  const [deposit, r2] = useAsync(key, (signal) => fianzaService.getDeposit(stay, apiLang, signal))
  const [recommendation, r3] = useAsync(key, (signal) => extrasService.getRecommendation(stay, stay.phase, signal))
  const [keyStatus, r4] = useAsync(key, (signal) => accesosService.getKey(stay, signal))
  const [climate, r5] = useAsync(key, (signal) => climaService.getClimate(stay, apiLang, signal))
  const [arrival, r6] = useAsync(key, (signal) => checkinService.getArrival(stay, signal))
  const [pendingChange, r7] = useAsync(`${key}|${stay.dates.checkIn}|${stay.dates.checkOut}`, (signal) => reservaService.getPendingChange(stay, apiLang, signal))
  const reload = useCallback(() => {
    r1()
    r2()
    r3()
    r4()
    r5()
    r6()
    r7()
  }, [r1, r2, r3, r4, r5, r6, r7])
  return { payment, deposit, recommendation, key: keyStatus, climate, arrival, pendingChange, reload }
}
